/** Freeze / unfreeze confirmation, shared by the admin and employee drawers. */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { ConfirmDialog, toast } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Alert02Icon,
  CreditCardDefrostIcon,
  CreditCardFreezeIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { FREEZE_CARD, UNFREZEE_CARD } from "graphql/card";
import { getGraphqlErrorMessage } from "utils/graphql";
import CardStatusType from "enums/CardStatusType";
import CardType from "enums/CardType";

type FreezeCardDialogProps = {
  open: boolean;
  cardId: string | null;
  cardStatus?: string;
  cardType?: string;
  onClose: () => void;
  /** Called after the mutation succeeds (e.g. refetch the card detail). */
  onCompleted: () => void;
  /** Reports the mutation in flight so the parent drawer can gate its close paths. */
  onBusyChange?: (busy: boolean) => void;
};

const FreezeCardDialog: React.FC<FreezeCardDialogProps> = ({
  open,
  cardId,
  cardStatus,
  cardType,
  onClose,
  onCompleted,
  onBusyChange,
}) => {
  const { t } = useTranslation();

  // ACTIVE cards freeze; anything else reaching this dialog (FROZEN) unfreezes.
  const isFreeze = cardStatus === CardStatusType.ACTIVE;
  const isExternal = cardType === CardType.EXTERNAL;

  // Each mutation names its own result: by the time it completes, the cached
  // card status has already flipped, so cardStatus says the opposite.
  const completed = (message: string) => () => {
    toast.success(message);
    onCompleted();
    onClose();
  };

  const [freezeCard, { loading: freezing }] = useMutation(FREEZE_CARD, {
    onCompleted: completed(t("Card frozen")),
    onError: (error) => {
      toast.error(getGraphqlErrorMessage(error?.graphQLErrors));
    },
  });
  const [unFreezeCard, { loading: unfreezing }] = useMutation(UNFREZEE_CARD, {
    onCompleted: completed(t("Card unfrozen")),
    onError: (error) => {
      toast.error(getGraphqlErrorMessage(error?.graphQLErrors));
    },
  });

  const handleConfirm = () => {
    if (!cardId) return;
    const mutate = isFreeze ? freezeCard : unFreezeCard;
    mutate({ variables: { id: cardId } });
  };

  const { title, description, confirmLabel, icon, tone } = (() => {
    if (!isFreeze) {
      return {
        title: t("Unfreeze Card"),
        description: t("You are about to Unfreeze this card"),
        confirmLabel: t("Unfreeze"),
        icon: CreditCardDefrostIcon,
        tone: "primary" as const,
      };
    }
    if (isExternal) {
      return {
        title: t("Stop Import of Transactions"),
        description: t(
          "You are about to stop importing transactions for this card",
        ),
        confirmLabel: t("Freeze"),
        icon: Alert02Icon,
        tone: "danger" as const,
      };
    }
    return {
      title: t("Freeze Card"),
      description: t(
        "Freezing your card stops all purchases and transactions You can unfreeze your card at any time",
      ),
      confirmLabel: t("Freeze"),
      icon: CreditCardFreezeIcon,
      tone: "danger" as const,
    };
  })();

  const busy = freezing || unfreezing;

  // The parent drawer gates its close paths on this; the cleanup clears it so an
  // unmount mid-flight can't leave the drawer stuck.
  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);

  return (
    <ConfirmDialog
      open={open}
      // The DS only swallows a backdrop click while busy; the close X and
      // Escape still fire, so the gate is ours to hold.
      onCancel={() => {
        if (busy) return;
        onClose();
      }}
      title={title}
      message={description}
      confirmLabel={confirmLabel}
      cancelLabel={t("Cancel")}
      icon={<HugeiconsIcon icon={icon} size={20} color="currentColor" />}
      tone={tone}
      busy={busy}
      onConfirm={handleConfirm}
    />
  );
};

export default FreezeCardDialog;
